import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Image,
  TouchableOpacity,
} from "react-native";
import { Image as ExpoImage } from "expo-image";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams, useFocusEffect } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { Team, Profile } from "@/types";
import { AvatarPreviewModal } from "@/components/AvatarPreviewModal";

/**
 * Team member with profile data
 */
interface TeamMember {
  id: string;
  user_id: string;
  is_captain: boolean;
  joined_at: string;
  profile: Pick<Profile, "id" | "full_name" | "avatar_url">;
}

/**
 * Extended team with members
 */
interface TeamWithMembers extends Team {
  members: TeamMember[];
}

/**
 * Team Detail Page
 */
export default function TeamDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { activeProfile, userTeam, roles } = useAuth();
  const colors = useThemeColors();

  const [team, setTeam] = useState<TeamWithMembers | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  
  // Avatar preview state
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showAvatarPreview, setShowAvatarPreview] = useState(false);

  /**
   * Handle avatar tap - show preview modal
   */
  const handleAvatarTap = (member: TeamMember) => {
    setSelectedMember(member);
    setShowAvatarPreview(true);
  };

  /**
   * Handle view profile from modal
   */
  const handleViewProfileFromModal = (userId: string) => {
    router.push(`/(tabs)/profile/user/${userId}` as any);
  };

  const isUserTeam = userTeam?.id === id;
  const teamColor = team?.color || colors.primary;
  
  // Can customize if: user is a team member OR is a familie admin
  const canCustomize = isUserTeam || isTeamMember || roles.isFamilieAdmin;

  /**
   * Fetch team data
   */
  const fetchTeam = useCallback(async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("teams")
        .select(`
          *,
          team_members (
            id,
            user_id,
            is_captain,
            joined_at,
            profiles:user_id (
              id,
              full_name,
              avatar_url
            )
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;

      // Transform data
      const transformedTeam: TeamWithMembers = {
        ...data,
        members: data.team_members?.map((tm: any) => ({
          ...tm,
          profile: tm.profiles,
        })) || [],
      };

      // Sort: captain first, then alphabetically
      transformedTeam.members.sort((a, b) => {
        if (a.is_captain && !b.is_captain) return -1;
        if (!a.is_captain && b.is_captain) return 1;
        return (a.profile.full_name || "").localeCompare(b.profile.full_name || "");
      });

      setTeam(transformedTeam);
      
      // Check if current user (or switched dependent) is a member of this team
      const isMember = transformedTeam.members.some((m) => m.user_id === activeProfile?.id);
      setIsTeamMember(isMember);
    } catch (error) {
      console.error("Error fetching team:", error);
      Alert.alert("Fout", "Kon team niet laden");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Refresh when screen comes into focus (e.g., returning from customize/avatar-creator)
  useFocusEffect(
    useCallback(() => {
      // Only refresh if we already have team data (not initial load)
      if (team) {
        fetchTeam();
      }
    }, [team?.id])
  );

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchTeam();
  };

  /**
   * Navigate to customize team (for team members)
   */
  const handleCustomize = () => {
    router.push(`/(tabs)/teams/customize?id=${id}` as any);
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Team laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!team) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center px-8"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <Ionicons name="alert-circle-outline" size={64} color={colors.border} />
        <Text
          className="text-xl font-semibold mt-4"
          style={{ color: colors.text }}
        >
          Team niet gevonden
        </Text>
        <Pressable
          onPress={() => router.back()}
          className="mt-6 rounded-xl px-6 py-3"
          style={{ backgroundColor: colors.primaryLight }}
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Text className="font-medium" style={{ color: colors.primary }}>
            Terug
          </Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  const captain = team.members.find((m) => m.is_captain);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Header with back button */}
        <View className="px-4 py-3 flex-row items-center">
          <Pressable
            onPress={() => router.back()}
            className="mr-4"
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </Pressable>
          <Text
            className="text-lg font-semibold flex-1"
            style={{ color: colors.text }}
          >
            Team Details
          </Text>
          {canCustomize && (
            <Pressable
              onPress={handleCustomize}
              className="rounded-lg px-3 py-2 flex-row items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              accessibilityRole="button"
              accessibilityLabel="Team aanpassen"
            >
              <Ionicons name="color-palette-outline" size={18} color={colors.primary} />
              <Text className="font-medium ml-1" style={{ color: colors.primary }}>
                Aanpassen
              </Text>
            </Pressable>
          )}
        </View>

        {/* Team Hero */}
        <View
          className="mx-4 rounded-2xl overflow-hidden"
          style={{ backgroundColor: `${teamColor}10` }}
        >
          {/* Color bar */}
          <View className="h-2" style={{ backgroundColor: teamColor }} />

          <View className="p-6 items-center">
            {/* Avatar */}
            <View
              className="w-24 h-24 rounded-full items-center justify-center mb-4 border-4 overflow-hidden"
              style={{ backgroundColor: `${teamColor}20`, borderColor: teamColor }}
            >
              {team.avatar_url ? (
                // Check if it's an emoji (single character or emoji) or URL
                team.avatar_url.startsWith("http") ? (
                  <Image
                    source={{ uri: team.avatar_url }}
                    className="w-full h-full"
                    resizeMode="cover"
                  />
                ) : (
                  <Text className="text-5xl">{team.avatar_url}</Text>
                )
              ) : (
                <Text className="text-4xl font-bold" style={{ color: teamColor }}>
                  {team.name.charAt(0).toUpperCase()}
                </Text>
              )}
            </View>

            {/* Name & Slogan */}
            <Text className="text-2xl font-bold" style={{ color: colors.text }}>
              {team.name}
            </Text>
            {team.description && (
              <Text
                className="italic mt-1 text-center"
                style={{ color: colors.textSecondary }}
              >
                "{team.description}"
              </Text>
            )}

            {/* Score */}
            <View className="mt-4 flex-row items-center">
              <Ionicons name="trophy" size={24} color={teamColor} />
              <Text
                className="text-3xl font-bold ml-2"
                style={{ color: teamColor }}
              >
                {team.total_score}
              </Text>
              <Text className="ml-1" style={{ color: colors.textTertiary }}>
                punten
              </Text>
            </View>

            {/* Badge if user's team */}
            {isUserTeam && (
              <View
                className="mt-4 rounded-full px-4 py-2"
                style={{ backgroundColor: `${teamColor}20` }}
              >
                <Text style={{ color: teamColor }} className="font-medium">
                  Dit is jouw team! 🎉
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Captain Section */}
        {captain && (
          <View className="px-4 mt-6">
            <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
              Aanvoerder
            </Text>
            <View
              className="rounded-xl p-4 flex-row items-center"
              style={{
                backgroundColor: "#FEF3C740",
                borderWidth: 1,
                borderColor: "#FCD34D",
              }}
            >
              <View className="w-12 h-12 rounded-full bg-amber-100 items-center justify-center mr-3">
                <Text className="text-amber-700 font-bold text-lg">
                  {captain.profile.full_name?.charAt(0).toUpperCase() || "?"}
                </Text>
              </View>
              <View className="flex-1">
                <Text className="font-semibold" style={{ color: colors.text }}>
                  {captain.profile.full_name || "Onbekend"}
                </Text>
                <Text className="text-amber-600 text-sm">👑 Team Aanvoerder</Text>
              </View>
            </View>
          </View>
        )}

        {/* Team Members */}
        <View className="px-4 mt-6">
          <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
            Teamleden ({team.members.length})
          </Text>
          <View
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            {team.members.map((member, index) => {
              const isCurrentUser = member.user_id === activeProfile?.id;

              /**
               * Navigate to user profile
               */
              const handleViewProfile = () => {
                router.push(`/(tabs)/profile/user/${member.user_id}` as any);
              };

              return (
                <Pressable
                  key={member.id}
                  onPress={handleViewProfile}
                  className="flex-row items-center px-4 py-3 active:opacity-70"
                  style={{
                    borderBottomWidth: index < team.members.length - 1 ? 1 : 0,
                    borderBottomColor: colors.border,
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`Bekijk profiel van ${member.profile.full_name || "Onbekend"}`}
                >
                  {/* Avatar - Tap to enlarge */}
                  <TouchableOpacity
                    onPress={() => handleAvatarTap(member)}
                    className="relative mr-3"
                    accessibilityLabel={`Bekijk foto van ${member.profile.full_name || "gebruiker"}`}
                    accessibilityRole="button"
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center overflow-hidden"
                      style={{ backgroundColor: `${teamColor}15` }}
                    >
                      {member.profile.avatar_url ? (
                        <ExpoImage
                          source={{ uri: member.profile.avatar_url }}
                          style={{ width: 40, height: 40 }}
                          contentFit="cover"
                        />
                      ) : (
                        <Text className="font-medium" style={{ color: teamColor }}>
                          {member.profile.full_name?.charAt(0).toUpperCase() || "?"}
                        </Text>
                      )}
                    </View>
                    {member.is_captain && (
                      <View className="absolute -top-1 -right-1 bg-amber-400 rounded-full p-0.5">
                        <Ionicons name="star" size={10} color="#FFFFFF" />
                      </View>
                    )}
                  </TouchableOpacity>

                  {/* Name */}
                  <View className="flex-1">
                    <View className="flex-row items-center">
                      <Text
                        className={isCurrentUser ? "font-semibold" : ""}
                        style={{ color: colors.text }}
                      >
                        {member.profile.full_name || "Onbekend"}
                      </Text>
                      {isCurrentUser && (
                        <View
                          className="rounded px-1.5 py-0.5 ml-2"
                          style={{ backgroundColor: colors.primaryLight }}
                        >
                          <Text
                            className="text-xs font-medium"
                            style={{ color: colors.primary }}
                          >
                            Jij
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>

                  {/* Captain badge */}
                  {member.is_captain && (
                    <View className="bg-amber-100 rounded-full px-2 py-0.5 mr-2">
                      <Text className="text-amber-700 text-xs font-medium">
                        Aanvoerder
                      </Text>
                    </View>
                  )}

                  {/* Chevron to indicate clickable */}
                  <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} />
                </Pressable>
              );
            })}
          </View>
        </View>

        <View className="h-8" />
      </ScrollView>

      {/* Avatar Preview Modal */}
      <AvatarPreviewModal
        visible={showAvatarPreview}
        avatarUrl={selectedMember?.profile.avatar_url}
        userName={selectedMember?.profile.full_name}
        userId={selectedMember?.user_id}
        onClose={() => {
          setShowAvatarPreview(false);
          setSelectedMember(null);
        }}
        onViewProfile={handleViewProfileFromModal}
      />
    </SafeAreaView>
  );
}
